<template>
  <section class="notifications-orders-item">
    <div class="notifications-orders-item__row notifications-orders-item__row_btw">
      <span class="notifications-orders-item__number">Заказ № {{ number }}</span>
      <span class="notifications-orders-item__products-count">
        {{ productsCount }} {{ $filters.decl(productsCount, 'товар', ['', 'a', 'ов']) }}
      </span>
    </div>
    <div class="notifications-orders-item__row">
      <div
        class="notifications-orders-item__status"
        :class="statusClasses"
      >
        {{ getOrderStatus(status) }}
      </div>
      <div
        v-if="isWaitingPrepay"
        class="notifications-orders-item__status notifications-orders-item__status_red"
      >
        Ожидает оплаты
      </div>
    </div>
    <div
      v-if="isWaitingPrepay && !countdown.isTimeOut.value"
      class="notifications-orders-item__row notifications-orders-item__prepay-text"
    >
      Требуется предоплата. Заказ будет отменён через&nbsp;
      <b>{{ padCount(countdown.secondCell.value.digit) }}:{{ padCount(countdown.thirdCell.value.digit) }}</b>
    </div>
    <div class="notifications-orders-item__row notifications-orders-item__row_top">
      <f-sprite-icon
        v-if="receivingMethodIcon"
        class="notifications-orders-item__receiving-icon"
        :class="{
          'notifications-orders-item__receiving-icon_offset': receivingMethod !== DELIVERY_TYPES.delivery,
        }"
        :icon-link="receivingMethodIcon"
      />
      <span class="notifications-orders-item__receiving-text">{{ receivingMethodText }}</span>
    </div>
    <notifications-orders-date
      class="notifications-orders-item__row"
      :status="status"
      :receiving-method="receivingMethod"
      :receiving-date-from="receivingDateFrom"
      :receiving-date-to="receivingDateTo"
      :storage-period="storagePeriod"
      :delivery-time="deliveryTime"
    />
  </section>
</template>

<script lang="ts">
import {
  computed, defineComponent, onMounted, watch,
} from 'vue';

export default defineComponent({
  name: 'notifications-orders-item',
});
</script>

<script setup lang="ts">
import { useStore } from 'vuex';

import useCountdown from '@/composables/countdown/countdown';
import { DELIVERY_TYPES, NOTIFICATIONS_ORDER_ICONS, NOTIFICATIONS_ORDER_RECEIVING_METHOD } from '@/constants/delivery';
import { getOrderStatus, STATUSES_TYPES } from '@/utils/order-status';

import NotificationsOrdersDate from './notifications-orders-date.vue';

interface IProps {
  createdAt: string,
  number: string,
  productsCount: number,
  status: string,
  isWaitingPrepay: boolean,
  receivingMethod: string,
  address: string,
  externalDeliveryName: string | null,
  receivingDateFrom: string | null,
  receivingDateTo: string | null,
  storagePeriod: string | null,
  deliveryTime: string | null,
}

const props = withDefaults(defineProps<IProps>(), {
  externalDeliveryName: null,
  receivingDateFrom: null,
  receivingDateTo: null,
  storagePeriod: null,
  deliveryTime: null,
});

const emit = defineEmits(['prepay-timer-end']);

const countdown = useCountdown();
const store = useStore();

const statusClasses = computed(() => ({
  'notifications-orders-item__status_blue': props.status === STATUSES_TYPES.at_pickup || props.status === STATUSES_TYPES.ready_to_issue,
}));

const prepayTimeMs = computed<number>(() => store.getters['settings/prepayTime'] * 60 * 1000);

const receivingMethodIcon = computed<string>(() => NOTIFICATIONS_ORDER_ICONS[props.receivingMethod]);

const receivingMethodText = computed<string>(() => {
  let text = NOTIFICATIONS_ORDER_RECEIVING_METHOD[props.receivingMethod];

  if (props.externalDeliveryName) {
    text += ` ${props.externalDeliveryName}:`;
  } else if (props.receivingMethod !== DELIVERY_TYPES.delivery) {
    text += ':';
  }

  return `${text} ${props.address}`;
});

const padCount = (digit) => digit.toString().padStart(2, '0');

onMounted(() => {
  if (props.isWaitingPrepay) {
    countdown.startCountdown((new Date(props.createdAt)).getTime() + prepayTimeMs.value);

    if (countdown.isTimeOut) {
      emit('prepay-timer-end');
    }
  }
});

watch(countdown.isTimeOut, (val, oldVal) => {
  if (val && val !== oldVal) {
    emit('prepay-timer-end');
  }
});

</script>

<style lang="scss">
.notifications-orders-item {
  display: flex;
  flex-direction: column;
  padding: 12px;

  @mixin font($size: 14px, $font-weight: 600, $line-height: 20px) {
    font-size: $size;
    font-weight: $font-weight;
    line-height: $line-height;
  }

  & &__row {
    display: flex;

    &:not(:first-child) {
      margin-top: 4px;
    }

    &_btw {
      justify-content: space-between;
    }

    &_top {
      align-items: flex-start;
    }
  }

  & &__number {
    @include font();
    color: #333;
  }

  & &__products-count {
    @include font();
    color: #999;
  }

  & &__status {
    @include font(12px, 600, 16px);
    padding: 4px 8px;
    border-radius: 4px;
    border: 1px solid;
    color: #999;
    border-color: #999;

    &:not(:first-child) {
      margin-left: 4px;
    }

    &_blue {
      color: #1a82cb;
      border-color: #1a82cb;
    }

    &_red {
      color: #f34744;
      border-color: #f34744;
    }
  }

  & &__prepay-text {
    @include font(12px, 400, 20px);
    color: #f34744;
  }

  & &__receiving-icon {
    max-width: 18px;
    max-height: 18px;

    &_offset {
      margin-top: 1.5px;
    }
  }

  & &__receiving-text {
    margin-left: 8px;
    font-size: 12px;
    line-height: 20px;
    color: #333;
  }
}
</style>
